/**
 * 지출결의등록(type2)
 * 지정 HWP 결의서 양식에서 수기·팝업 입력 → 결의 테이블 + HWP 원문 저장
 * SSOT: D:\295\erp\BD\design\15_지출결의서_양식필드매핑.md
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { DateCalendarPopover, NumberField } from 'components/forms/elements';
import { toStorageDate } from 'utils/datePickerUtils';
import DraggableDialog from 'components/common/DraggableDialog';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { GridPanel, GridBody } from 'components/budget/BudgetFormationChrome.styles';
import { showAlert, showConfirm } from 'utils/toastBridge';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadTenants } from 'master/tenantStore';
import { getCachedVendors, getCachedAccounts, loadVendors, loadAccounts } from 'master/vendorStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import { loadSubjectsByFilter } from 'budget/budgetStructureStore';
import { createRhwpFormEditor } from 'budget/rhwp/rhwpEditorHost';
import {
  EXPENSE_STATUS,
  RESOLUTION_TYPE,
  expenseStatusLabel,
  createResolutionUid,
  createResolutionDetailUid,
  emptyResolutionMaster,
  emptyResolutionDetailRow,
  loadResolutionBundle,
  saveResolutionBundle,
  loadObligationBundle,
  saveObligationBundle,
  ensureObligationForResolution,
} from 'expense/expenseStore';
import {
  FORM_ID_DJ_WATERWORKS_V1,
  DJ_WATERWORKS_V1_FIELDS,
  getFieldDef,
  fieldValuesToResolution,
  resolutionToFieldValues,
  subjectChainToFieldValues,
  projectChainToFieldValues,
  vendorToFieldValues,
  vendorToDetailPatch,
  walkCodeChain,
  formatDate,
  parseDate,
  parseAmount,
  reconcileResolutionAmounts,
  amountPartsToFieldValues,
} from 'expense/resolutionFormFieldMap';
import {
  saveResolutionFormHwp,
  getResolutionFormHwp,
  deleteResolutionFormHwp,
} from 'expense/resolutionFormFileStore';
import { DOC_TYPE } from 'system/formDocTypes';
import { resolveForm, getForm } from 'system/formRegistryStore';
import { loadFormTemplateBytes } from 'system/formTemplateFileStore';

const LIST_GRID_ID = 'expense-res-t2-list';

const Page = styled(PageContainer)`
  padding: 4px 6px 6px;
  gap: 4px;
`;

const Stack = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 11px;
  color: var(--erp-text-secondary);
  line-height: 1.35;
`;

const ListHost = styled.div`
  flex: 0 0 140px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EditorHost = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  border: 1px solid var(--erp-border);
  border-radius: 4px;
  overflow: hidden;
  background: var(--erp-color-bg-white);

  & > iframe {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    border: none;
  }
`;

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

function isType2(row) {
  if (row?.mod === 'D') return false;
  const fid = String(row?.formId || '').trim();
  if (!fid) return false;
  if (fid === FORM_ID_DJ_WATERWORKS_V1) return true;
  const form = getForm(fid);
  return form?.docType === DOC_TYPE.EXP_RESOLUTION;
}

function formTemplateName(form) {
  return form?.templateFile || (form?.formId ? `${form.formId}.hwp` : 'resolution.hwp');
}

function syncFieldNames(form) {
  const fields = form?.fields?.length ? form.fields : DJ_WATERWORKS_V1_FIELDS;
  return fields.filter((f) => f.syncToDb).map((f) => f.fieldId);
}

function lookupGroupOf(fieldId) {
  const def = getFieldDef(fieldId);
  if (def?.lookupGroup) return def.lookupGroup;
  if (String(fieldId || '').startsWith('subject.')) return 'subject';
  if (String(fieldId || '').startsWith('project.')) return 'project';
  if (String(fieldId || '').startsWith('payee.')) return 'vendor';
  if (fieldId === 'bank.accountDisplay') return 'vendorAccount';
  if (fieldId === 'header.deptName') return 'dept';
  return '';
}

function isDateField(fieldId) {
  const def = getFieldDef(fieldId);
  if (def?.type === 'date') return true;
  const id = String(fieldId || '');
  return id === 'receipt.date' || /Date$/i.test(id);
}

function isAmountField(fieldId) {
  const def = getFieldDef(fieldId);
  if (def?.type === 'amount') return true;
  return String(fieldId || '').startsWith('amount.');
}

function calendarAnchorRect(hostEl) {
  const r = hostEl?.getBoundingClientRect?.();
  const cx = r ? r.left + r.width / 2 : window.innerWidth / 2;
  const cy = r ? r.top + 56 : window.innerHeight / 3;
  return { top: cy, left: cx - 140, bottom: cy + 8, right: cx + 140 };
}

const AmtDialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 2px 0;
`;

const AmtHint = styled.p`
  margin: 0;
  font-size: 11px;
  color: var(--erp-text-secondary);
  line-height: 1.4;
`;

const AmtActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 4px;
`;

const AMT_LABEL_W = '72px';

function AmountCalcDialog({ open, payTotalAmt, fee, focusField, prefsUserId, onClose, onApply }) {
  const [draftPay, setDraftPay] = useState(payTotalAmt || 0);
  const [draftFee, setDraftFee] = useState(fee || 0);

  useEffect(() => {
    if (!open) return;
    setDraftPay(Number(payTotalAmt) || 0);
    setDraftFee(Number(fee) || 0);
  }, [open, payTotalAmt, fee]);

  const parts = reconcileResolutionAmounts({ payTotalAmt: draftPay, fee: draftFee });

  const apply = () => onApply?.(parts);

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      apply();
    }
  };

  return (
    <DraggableDialog
      open={open}
      title="금액"
      onClose={onClose}
      dialogWidth="420px"
      dialogHeight="360px"
      prefsUserId={prefsUserId}
      sizeKey="res-t2-amount"
    >
      <AmtDialogBody>
        <AmtHint>
          총 지급액(공급가+부가세)과 수수료를 입력합니다. 계는 총 지급액+수수료로 자동 반영되며, 총 지급액은 양식에 쓰지 않습니다.
        </AmtHint>
        <NumberField
          name="total"
          label="계"
          value={parts.total}
          readonly
          labelWidth={AMT_LABEL_W}
          width={CommonCss.w120}
        />
        <NumberField
          name="payTotalAmt"
          label="총 지급액"
          value={draftPay}
          min={0}
          labelWidth={AMT_LABEL_W}
          width={CommonCss.w120}
          fieldProps={focusField === 'fee' ? {} : { autoFocus: true }}
          onChange={(e) => setDraftPay(Number(e.target.value) || 0)}
          onKeyDown={onKeyDown}
        />
        <NumberField
          name="supply"
          label="공급가"
          value={parts.supply}
          readonly
          labelWidth={AMT_LABEL_W}
          width={CommonCss.w120}
        />
        <NumberField
          name="vat"
          label="부가세"
          value={parts.vat}
          readonly
          labelWidth={AMT_LABEL_W}
          width={CommonCss.w120}
        />
        <NumberField
          name="fee"
          label="수수료"
          value={draftFee}
          min={0}
          labelWidth={AMT_LABEL_W}
          width={CommonCss.w120}
          fieldProps={focusField === 'fee' ? { autoFocus: true } : {}}
          onChange={(e) => setDraftFee(Number(e.target.value) || 0)}
          onKeyDown={onKeyDown}
        />
        <AmtActions>
          <ActionButtonGroup buttons={[{ id: 'apply', label: '적용', onClick: apply }]} />
        </AmtActions>
      </AmtDialogBody>
    </DraggableDialog>
  );
}

export default function ExpenseResolutionType2Workspace() {
  return (
    <ActiveGridProvider>
      <ExpenseResolutionType2Inner />
    </ActiveGridProvider>
  );
}

function ExpenseResolutionType2Inner() {
  const tenantCode = useTenantCode();
  const { user } = useAuth();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const hostRef = useRef(null);
  const editorRef = useRef(null);
  const activeFormRef = useRef(null);
  const listGridRef = useRef(null);
  const currentUidRef = useRef('');
  const pickedRef = useRef({});
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [currentUid, setCurrentUid] = useState('');
  const [orgName, setOrgName] = useState('');
  const [activeForm, setActiveForm] = useState(null);
  const [chromeHidden, setChromeHidden] = useState(true);
  const [lookup, setLookup] = useState({ open: false, type: '' });
  const [datePick, setDatePick] = useState({ open: false, fieldId: '', value: '' });
  const [amountPick, setAmountPick] = useState({ open: false, focus: 'pay', payTotalAmt: 0, fee: 0 });
  const [deptRows, setDeptRows] = useState([]);
  const [subjectRows, setSubjectRows] = useState([]);
  const [projectRows, setProjectRows] = useState([]);

  const methods = useForm({
    defaultValues: { fiscalYear: getCurrentFiscalYear() },
  });
  const { getValues } = methods;

  const listGrid = useHiGridHook({
    gridRef: listGridRef,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: false,
    defaultRow: {},
  });

  const dims = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || getCurrentFiscalYear()).trim(),
    }),
    [tenantCode, getValues],
  );

  /** resolveForm · ACL용 작성 컨텍스트 */
  const buildResolveCtx = useCallback(() => {
    const d = dims();
    const p = pickedRef.current || {};
    return {
      tenantCode: d.tenantCode,
      fiscalYear: d.fiscalYear,
      deptCode: p.deptCode || '',
      projectCode: p.projectCode || '',
      fundCode: p.fundCode || '',
      userId: user?.id || '',
    };
  }, [dims, user]);

  const syncActiveForm = useCallback(
    async ({ reloadIfChanged = false } = {}) => {
      const form = resolveForm(DOC_TYPE.EXP_RESOLUTION, buildResolveCtx());
      const prevId = activeFormRef.current?.formId;
      activeFormRef.current = form;
      setActiveForm(form);
      if (!form) {
        setLoadError(
          '사용 가능한 지출결의 양식이 없습니다. 환경설정 › 매칭 규칙·사용 권한을 확인하세요.',
        );
        return null;
      }
      setLoadError('');
      if (reloadIfChanged && form.formId !== prevId && editorRef.current) {
        try {
          const loaded = await loadFormTemplateBytes(form);
          await editorRef.current.loadFile(loaded.bytes, loaded.fileName, {
            suppressDialogs: true,
            skipUnsavedGuard: true,
          });
          showAlert(`매칭 양식: ${form.formName || form.formId}`, 'info');
        } catch (e) {
          showAlert(e?.message || '양식 전환 실패', 'error');
        }
      }
      return form;
    },
    [buildResolveCtx],
  );

  const listColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '결의번호', field: 'resolutionNo', size: 130 }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 120 }),
      hiCol.roTextColumn({ header: '사업', field: 'projectName', size: 160 }),
      hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 140 }),
      hiCol.roNumberColumn({ header: '금액', field: 'resolutionAmt', size: 110 }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80 }),
      hiCol.roTextColumn({ header: '적요', field: 'remark', size: 180 }),
    ],
    [],
  );

  const enrich = useCallback(
    (m) => ({
      ...m,
      id: m.resolutionUid,
      statusNm: expenseStatusLabel(m.status),
    }),
    [],
  );

  const applyFields = useCallback(async (fieldValues, fileName) => {
    const ed = editorRef.current;
    if (!ed) return false;
    const name = fileName || formTemplateName(activeFormRef.current);
    const result = await ed.putFieldMap(fieldValues, name);
    const failed = (result.results || []).filter((r) => !r.ok);
    if (failed.length) {
      showAlert(`일부 칸 반영 실패 (${failed.map((f) => f.name).join(', ')})`, 'warning');
      return false;
    }
    return true;
  }, []);

  const loadTemplate = useCallback(async () => {
    const ed = editorRef.current;
    if (!ed) return false;
    const form =
      (await syncActiveForm()) ||
      activeFormRef.current ||
      resolveForm(DOC_TYPE.EXP_RESOLUTION, buildResolveCtx());
    if (!form) throw new Error('지출결의 양식이 없습니다. 환경설정 › 양식 레지스트리에서 지정하세요.');
    const loaded = await loadFormTemplateBytes(form);
    await ed.loadFile(loaded.bytes, loaded.fileName, {
      suppressDialogs: true,
      skipUnsavedGuard: true,
    });
    return true;
  }, [syncActiveForm, buildResolveCtx]);

  const systemFieldValues = useCallback(
    (fy, extra = {}) => ({
      'header.fiscalYear': String(fy || ''),
      'header.orgName': orgName,
      'process.proposeDate': formatDate(todayYmd()),
      ...extra,
    }),
    [orgName],
  );

  const handleSearch = useCallback(async () => {
    const d = dims();
    try {
      const bundle = await loadResolutionBundle(d);
      const rows = (bundle.masters || []).filter(isType2).map(enrich);
      listGridRef.current?.setData?.(rows);
      return rows;
    } catch (err) {
      console.error('[type2] resolution list', err);
      listGridRef.current?.setData?.([]);
      showAlert(
        `결의 목록 조회 실패: ${err?.message || err}\n(BE BdExpense.selectResolutionList · Flyway V22·테이블 확인)`,
        'error',
      );
      return [];
    }
  }, [dims, enrich]);

  const openResolution = useCallback(
    async (row) => {
      const uid = String(row?.resolutionUid || '').trim();
      if (!uid || !editorRef.current) return;
      setBusy(true);
      try {
        const d = dims();
        const bundle = await loadResolutionBundle(d);
        const master = (bundle.masters || []).find((m) => m.resolutionUid === uid && m.mod !== 'D');
        const detail =
          (bundle.details || []).find((x) => x.resolutionUid === uid && x.mod !== 'D') ||
          emptyResolutionDetailRow({ resolutionUid: uid });
        if (!master) {
          showAlert('결의를 찾을 수 없습니다.', 'warning');
          return;
        }
        currentUidRef.current = uid;
        setCurrentUid(uid);
        pickedRef.current = {
          deptCode: master.deptCode || '',
          deptName: master.deptName || '',
          projectCode: master.projectCode || '',
          vendorCode: detail.vendorCode || '',
        };
        const snapForm = master.formId ? getForm(master.formId) : null;
        if (snapForm) {
          activeFormRef.current = snapForm;
          setActiveForm(snapForm);
        } else {
          await syncActiveForm();
        }
        const rec = await getResolutionFormHwp(uid);
        if (rec?.hwpBytes?.length) {
          await editorRef.current.loadFile(
            rec.hwpBytes,
            rec.fileName || formTemplateName(activeFormRef.current),
            {
              suppressDialogs: true,
              skipUnsavedGuard: true,
            },
          );
        } else {
          await loadTemplate();
          await applyFields(resolutionToFieldValues(master, detail));
        }
      } catch (err) {
        console.error(err);
        showAlert(`열기 실패: ${err?.message || err}`, 'error');
      } finally {
        setBusy(false);
      }
    },
    [dims, loadTemplate, applyFields, syncActiveForm],
  );

  const handleAdd = useCallback(async () => {
    const ed = editorRef.current;
    if (!ed) {
      showAlert('에디터가 준비되지 않았습니다.', 'warning');
      return;
    }
    setBusy(true);
    try {
      currentUidRef.current = '';
      setCurrentUid('');
      pickedRef.current = {};
      listGridRef.current?.setSelectedRow?.(null);
      await loadTemplate();
      const fy = dims().fiscalYear;
      await applyFields(systemFieldValues(fy));
      showAlert('새 결의서 양식을 열었습니다. 저장 시 결의번호가 채번됩니다.', 'info');
    } catch (err) {
      showAlert(`양식 열기 실패: ${err?.message || err}`, 'error');
    } finally {
      setBusy(false);
    }
  }, [dims, loadTemplate, applyFields, systemFieldValues]);

  const handleDelete = useCallback(async () => {
    const selected = listGridRef.current?.getSelectedRow?.() || {};
    const uid = String(selected.resolutionUid || currentUidRef.current || '').trim();
    if (!uid) {
      showAlert('삭제할 결의를 목록에서 선택하세요.', 'warning');
      return;
    }
    const d = dims();
    const bundle = await loadResolutionBundle(d);
    const master = (bundle.masters || []).find((m) => m.resolutionUid === uid);
    if (master && master.status !== EXPENSE_STATUS.DRAFT && master.status !== EXPENSE_STATUS.REJECTED) {
      showAlert('작성중·반려 건만 삭제할 수 있습니다.');
      return;
    }
    const ok = await showConfirm(`결의 ${master?.resolutionNo || uid} 을(를) 삭제할까요?`);
    if (!ok) return;
    const masters = (bundle.masters || []).map((m) =>
      m.resolutionUid === uid ? { ...m, mod: 'D' } : m,
    );
    const details = (bundle.details || []).map((x) =>
      x.resolutionUid === uid ? { ...x, mod: 'D' } : x,
    );
    await saveResolutionBundle(d, masters, details);
    await deleteResolutionFormHwp(uid);
    if (currentUidRef.current === uid) {
      currentUidRef.current = '';
      setCurrentUid('');
      await handleAdd();
    }
    await handleSearch();
    showAlert('삭제했습니다.', 'success');
  }, [dims, handleSearch, handleAdd]);

  const handleSave = useCallback(async () => {
    const ed = editorRef.current;
    if (!ed) {
      showAlert('에디터가 준비되지 않았습니다.', 'warning');
      return;
    }
    setBusy(true);
    try {
      const form =
        activeFormRef.current ||
        resolveForm(DOC_TYPE.EXP_RESOLUTION, buildResolveCtx());
      if (!form) {
        showAlert('사용 가능한 양식이 없습니다. 매칭·사용 권한을 확인하세요.', 'warning');
        return;
      }
      const names = syncFieldNames(form);
      const fieldValues = await ed.extractFields(names);
      const d = dims();
      const bundle = await loadResolutionBundle(d);
      const uid = String(currentUidRef.current || '').trim() || createResolutionUid();
      const existingMaster =
        (bundle.masters || []).find((m) => m.resolutionUid === uid) || emptyResolutionMaster();
      const existingDetail =
        (bundle.details || []).find((x) => x.resolutionUid === uid && x.mod !== 'D') ||
        emptyResolutionDetailRow();
      const mapped = fieldValuesToResolution(fieldValues, { existingMaster, existingDetail });
      let master = emptyResolutionMaster({
        ...mapped.master,
        resolutionUid: uid,
        formId: form?.formId || existingMaster.formId || FORM_ID_DJ_WATERWORKS_V1,
        formMapVersion: form?.mapVersion || existingMaster.formMapVersion || 1,
        resolutionType: mapped.master.resolutionType || RESOLUTION_TYPE.GENERAL,
        status: mapped.master.status || EXPENSE_STATUS.DRAFT,
        fiscalYear: d.fiscalYear,
        deptCode: pickedRef.current.deptCode || mapped.master.deptCode || '',
        deptName: mapped.master.deptName || pickedRef.current.deptName || '',
        orgName: mapped.master.orgName || orgName,
        registerDate: mapped.master.registerDate || todayYmd(),
        resolutionDate: mapped.master.proposeDate || mapped.master.resolutionDate || todayYmd(),
        mod: existingMaster.resolutionUid ? 'U' : 'A',
      });
      let detail = emptyResolutionDetailRow({
        ...mapped.detail,
        detailUid: existingDetail.detailUid || createResolutionDetailUid(),
        resolutionUid: uid,
        vendorCode: pickedRef.current.vendorCode || mapped.detail.vendorCode || '',
        mod: existingDetail.detailUid ? 'U' : 'A',
      });
      const amounts = reconcileResolutionAmounts({
        payTotalAmt: master.payTotalAmt,
        supply: master.supplyAmt,
        vat: detail.vatAmt,
        fee: master.feeAmt,
        total: master.resolutionAmt,
      });
      master.resolutionAmt = amounts.total;
      master.payTotalAmt = amounts.payTotalAmt;
      master.supplyAmt = amounts.supply;
      master.feeAmt = amounts.fee;
      detail.amount = amounts.total;
      detail.vatAmt = amounts.vat;
      detail.vatYn = amounts.vat > 0 ? 'Y' : 'N';
      if (!String(master.subjectCode || '').trim() && !String(master.remark || '').trim() && !Number(master.resolutionAmt)) {
        showAlert('과목·적요·금액 중 하나 이상 입력하세요.', 'warning');
        return;
      }

      const oblBundle = await loadObligationBundle(d);
      const ensured = ensureObligationForResolution(d, master, oblBundle, bundle);
      master = ensured.resolutionMaster;
      await saveObligationBundle(
        d,
        ensured.obligationBundle.masters,
        ensured.obligationBundle.vendors,
        ensured.obligationBundle.contracts,
      );

      await applyFields({
        'doc.evidenceNo': master.resolutionNo || '',
        ...amountPartsToFieldValues(amounts),
      });
      const hwpBytes = await ed.exportHwp();
      const tmplName = formTemplateName(form);
      await saveResolutionFormHwp(uid, hwpBytes, tmplName);
      await ed.notifySaved?.(tmplName);

      const masters = [
        ...(bundle.masters || []).filter((m) => m.resolutionUid !== uid),
        master,
      ];
      const details = [
        ...(bundle.details || []).filter((x) => x.resolutionUid !== uid),
        detail,
      ];
      await saveResolutionBundle(d, masters, details);
      currentUidRef.current = uid;
      setCurrentUid(uid);
      await handleSearch();
      showAlert(`저장했습니다. 결의번호 ${master.resolutionNo}`, 'success');
    } catch (err) {
      console.error(err);
      showAlert(`저장 실패: ${err?.message || err}`, 'error');
    } finally {
      setBusy(false);
    }
  }, [dims, orgName, applyFields, handleSearch, buildResolveCtx]);

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions();

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7', title: '조회 (F7)' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: '새 결의서 양식 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: '선택 결의 삭제 (F9)',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
        disabled: busy || !ready,
        title: '양식 추출 → 결의 저장 (F10)',
      },
    ],
    [handleSearch, handleActiveAdd, handleActiveDelete, handleSave, busy, ready],
  );

  const extraButtons = useMemo(
    () => [
      {
        id: 'chrome',
        label: chromeHidden ? '도구모음' : '도구모음 숨김',
        onClick: () => {
          const nextHidden = !chromeHidden;
          setChromeHidden(nextHidden);
          editorRef.current?.setChrome?.(nextHidden ? 'minimal' : 'full');
        },
        title: chromeHidden
          ? '한글 메뉴·툴바·눈금자를 표시합니다'
          : '한글 메뉴·툴바·눈금자를 숨겨 양식 영역을 넓힙니다',
      },
    ],
    [chromeHidden],
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: CommonCss.w80,
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    [],
  );

  const onFieldClick = useCallback((payload) => {
    const name = payload?.name;
    if (isAmountField(name)) {
      setDatePick({ open: false, fieldId: '', value: '' });
      setLookup({ open: false, type: '' });
      (async () => {
        let payTotalAmt = 0;
        let fee = 0;
        try {
          const ed = editorRef.current;
          const [t, s, v, f] = await Promise.all([
            ed?.getFieldText?.('amount.total'),
            ed?.getFieldText?.('amount.supply'),
            ed?.getFieldText?.('amount.vat'),
            ed?.getFieldText?.('amount.fee'),
          ]);
          const parts = reconcileResolutionAmounts({
            total: parseAmount(t),
            supply: parseAmount(s),
            vat: parseAmount(v),
            fee: parseAmount(f),
          });
          payTotalAmt = parts.payTotalAmt;
          fee = parts.fee;
        } catch {
          payTotalAmt = 0;
          fee = 0;
        }
        setAmountPick({
          open: true,
          focus: name === 'amount.fee' ? 'fee' : 'pay',
          payTotalAmt,
          fee,
        });
      })();
      return;
    }
    const group = lookupGroupOf(name);
    if (group) {
      setDatePick({ open: false, fieldId: '', value: '' });
      setAmountPick({ open: false, focus: 'pay', payTotalAmt: 0, fee: 0 });
      setLookup({ open: true, type: group });
      return;
    }
    if (!isDateField(name)) return;
    (async () => {
      let current = '';
      try {
        const raw = await editorRef.current?.getFieldText?.(name);
        current = toStorageDate(parseDate(raw)) || '';
      } catch {
        current = '';
      }
      setLookup({ open: false, type: '' });
      setAmountPick({ open: false, focus: 'pay', payTotalAmt: 0, fee: 0 });
      setDatePick({
        open: true,
        fieldId: name,
        value: current,
      });
    })();
  }, []);
  const onFieldClickRef = useRef(onFieldClick);
  onFieldClickRef.current = onFieldClick;

  useEffect(() => {
    syncActiveForm();
  }, [syncActiveForm]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!hostRef.current) return;
        editorRef.current?.destroy?.();
        const host = await createRhwpFormEditor(hostRef.current, {
          chrome: 'minimal',
          onFieldClick: (payload) => onFieldClickRef.current?.(payload),
        });
        if (cancelled) {
          host.destroy?.();
          return;
        }
        editorRef.current = host;
        setReady(true);
        setLoadError('');
        const form =
          activeFormRef.current ||
          resolveForm(DOC_TYPE.EXP_RESOLUTION, buildResolveCtx());
        if (form) {
          activeFormRef.current = form;
          setActiveForm(form);
          try {
            const loaded = await loadFormTemplateBytes(form);
            if (!cancelled) {
              await host.loadFile(loaded.bytes, loaded.fileName, { suppressDialogs: true });
            }
          } catch (loadErr) {
            console.warn('[type2] template load', loadErr);
            if (!cancelled) setLoadError(loadErr?.message || '양식 로드 실패');
          }
        } else if (!cancelled) {
          setLoadError(
            '지출결의 양식이 없습니다. 환경설정 › 양식 레지스트리·매칭·사용 권한을 확인하세요.',
          );
        }
      } catch (e) {
        console.error('[type2] rhwp load failed', e);
        setLoadError(e?.message || '@rhwp/editor 로드 실패');
        setReady(false);
      }
    })();
    return () => {
      cancelled = true;
      editorRef.current?.destroy?.();
      editorRef.current = null;
    };
  }, [tenantCode]); // eslint-disable-line react-hooks/exhaustive-deps -- 호스트는 기관 변경 시에만 재생성

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fy = dims().fiscalYear;
      try {
        const [tenants, subjects, projects, depts] = await Promise.all([
          loadTenants(),
          loadSubjectsByFilter({ tenantCode, fiscalYear: fy, inoutDiv: '지출' }),
          loadOrSeedProjects({ tenantCode, fiscalYear: fy }),
          loadDepts(tenantCode),
          loadVendors(tenantCode),
          loadAccounts(tenantCode),
        ]);
        if (cancelled) return;
        const t = (Array.isArray(tenants) ? tenants : []).find(
          (r) => String(r.tenantCode) === String(tenantCode),
        );
        setOrgName(t?.tenantNm || '');
        setDeptRows(Array.isArray(depts) ? depts : []);
        setSubjectRows((subjects || []).filter((r) => r.useYn !== 'N' && r.mod !== 'D'));
        setProjectRows((projects || []).filter((r) => r.useYn !== 'N' && r.mod !== 'D'));
      } catch (e) {
        console.warn('[type2] master load', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantCode, dims]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  useEffect(() => {
    if (!ready || currentUidRef.current) return;
    applyFields(systemFieldValues(dims().fiscalYear)).catch(() => {});
  }, [ready, orgName, applyFields, systemFieldValues, dims]);

  useEffect(() => {
    listGrid.onRowClicked = ({ data }) => {
      if (data?.resolutionUid) openResolution(data);
    };
  }, [listGrid, openResolution]);

  const lookupConfig = useMemo(() => {
    if (lookup.type === 'dept') {
      const rows = filterDepts(deptRows, { useYn: 'Y' });
      return {
        title: '부서 조회',
        rows: rows.map((r) => ({ ...r, id: r.deptUid || r.deptCode })),
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 180 }),
        ],
        searchConfig: { mode: 'unified', keywordFields: ['deptCode', 'deptName'] },
      };
    }
    if (lookup.type === 'subject') {
      const leaf = subjectRows.filter((r) => r.finalYn === 'Y');
      const rows = (leaf.length ? leaf : subjectRows).map((r) => ({
        ...r,
        id: r.subjectUid || r.subjectCode,
      }));
      return {
        title: '예산과목 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '과목코드', field: 'subjectCode', size: 110 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 180 }),
          hiCol.roTextColumn({ header: '레벨', field: 'levelName', size: 70 }),
        ],
        searchConfig: { mode: 'unified', keywordFields: ['subjectCode', 'subjectName', 'levelName'] },
      };
    }
    if (lookup.type === 'project') {
      const leaf = projectRows.filter((r) => r.finalYn === 'Y');
      const rows = (leaf.length ? leaf : projectRows).map((r) => ({
        ...r,
        id: r.projectUid || r.projectCode,
      }));
      return {
        title: '사업 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 200 }),
          hiCol.roTextColumn({ header: '레벨', field: 'levelName', size: 70 }),
        ],
        searchConfig: { mode: 'unified', keywordFields: ['projectCode', 'projectName', 'levelName'] },
      };
    }
    if (lookup.type === 'vendor' || lookup.type === 'vendorAccount') {
      const vendors = (getCachedVendors(tenantCode) || []).filter((v) => v.useYn !== 'N');
      const accounts = getCachedAccounts(tenantCode) || [];
      if (lookup.type === 'vendorAccount') {
        const vc = pickedRef.current.vendorCode;
        const vendor = vendors.find((v) => v.vendorCode === vc);
        const rows = accounts
          .filter((a) => {
            if (a.useYn === 'N') return false;
            if (!vc) return true;
            return a.vendorUid === vendor?.vendorUid || a.vendorCode === vc;
          })
          .map((a) => {
            const v = vendors.find((x) => x.vendorUid === a.vendorUid) || {};
            return {
              ...a,
              id: a.accountUid || a.accountNo,
              vendorCode: v.vendorCode || a.vendorCode || '',
              vendorName: v.vendorName || '',
              ceoName: v.ceoName || '',
              address: v.address || '',
            };
          });
        return {
          title: '계좌 조회',
          rows,
          columns: [
            hiCol.roTextColumn({ header: '거래처', field: 'vendorName', size: 140 }),
            hiCol.roTextColumn({ header: '은행', field: 'bankName', size: 100 }),
            hiCol.roTextColumn({ header: '계좌번호', field: 'accountNo', size: 150 }),
            hiCol.roTextColumn({ header: '예금주', field: 'accountHolder', size: 120 }),
          ],
          searchConfig: { mode: 'unified', keywordFields: ['vendorName', 'bankName', 'accountNo'] },
        };
      }
      const rows = vendors.map((v) => {
        const acc =
          (accounts || []).find((a) => a.vendorUid === v.vendorUid && a.mainYn === 'Y') ||
          (accounts || []).find((a) => a.vendorUid === v.vendorUid);
        return {
          ...v,
          id: v.vendorUid || v.vendorCode,
          bankName: acc?.bankName || '',
          accountNo: acc?.accountNo || '',
          accountHolder: acc?.accountHolder || '',
        };
      });
      return {
        title: '거래처 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '코드', field: 'vendorCode', size: 90 }),
          hiCol.roTextColumn({ header: '상호', field: 'vendorName', size: 180 }),
          hiCol.roTextColumn({ header: '대표자', field: 'ceoName', size: 100 }),
          hiCol.roTextColumn({ header: '계좌', field: 'accountNo', size: 140 }),
        ],
        searchConfig: { mode: 'unified', keywordFields: ['vendorCode', 'vendorName', 'ceoName'] },
      };
    }
    return { title: '', rows: [], columns: [], searchConfig: {} };
  }, [lookup.type, tenantCode, deptRows, subjectRows, projectRows]);

  const onLookupSelect = useCallback(
    async (row) => {
      if (!row) return false;
      setBusy(true);
      try {
        if (lookup.type === 'dept') {
          pickedRef.current = { ...pickedRef.current, deptCode: row.deptCode || '', deptName: row.deptName || '' };
          await syncActiveForm({ reloadIfChanged: true });
          return applyFields({ 'header.deptName': row.deptName || '' });
        }
        if (lookup.type === 'subject') {
          const chain = walkCodeChain(row, subjectRows, { codeKey: 'subjectCode', parentKey: 'parentCode' });
          return applyFields(subjectChainToFieldValues(chain));
        }
        if (lookup.type === 'project') {
          const chain = walkCodeChain(row, projectRows, { codeKey: 'projectCode', parentKey: 'parentCode' });
          pickedRef.current = {
            ...pickedRef.current,
            projectCode: row.projectCode || '',
            projectName: row.projectName || '',
          };
          await syncActiveForm({ reloadIfChanged: true });
          return applyFields(projectChainToFieldValues(chain));
        }
        if (lookup.type === 'vendor') {
          pickedRef.current = { ...pickedRef.current, vendorCode: row.vendorCode || '' };
          const acc = {
            bankName: row.bankName,
            accountNo: row.accountNo,
            accountHolder: row.accountHolder,
          };
          return applyFields(vendorToFieldValues(row, acc));
        }
        if (lookup.type === 'vendorAccount') {
          if (row.vendorCode) pickedRef.current = { ...pickedRef.current, vendorCode: row.vendorCode };
          const vendor = {
            vendorCode: row.vendorCode,
            vendorName: row.vendorName,
            ceoName: row.ceoName,
            address: row.address,
          };
          const patch = vendorToDetailPatch(vendor, row);
          pickedRef.current = { ...pickedRef.current, vendorCode: patch.vendorCode };
          return applyFields(vendorToFieldValues(vendor, row));
        }
        return true;
      } finally {
        setBusy(false);
      }
    },
    [lookup.type, applyFields, subjectRows, projectRows, syncActiveForm],
  );

  return (
    <FormProvider {...methods}>
        <Page>
          <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
          <Stack>
            <CollapsibleSearchPanel
              storageKey="expense-res-t2-search"
              prefsUserId={prefsUserId}
              title="조회조건"
            >
              <FormArea fields={searchFields} compact />
            </CollapsibleSearchPanel>
            <Hint>
              type2: 결의서 양식은 resolveForm(부서·사업·ACL) 결과를 사용합니다
              {activeForm ? ` (${activeForm.formId})` : ''}.
              과목·사업·채주·부서는 조회 팝업, 일자는 캘린더, 금액은 총 지급액·수수료 팝업입니다.
              기본은 한글 도구모음을 숨깁니다. 「도구모음」으로 펼칠 수 있습니다.
              {activeLabel ? ` · 활성: ${activeLabel}` : ''}
              {currentUid ? ` · 편집 ${currentUid}` : ' · 신규(미저장)'}
              {loadError ? ` · ${loadError}` : ''}
            </Hint>
            <ListHost>
              <GridExportProvider>
                <GridExportFocusScope>
                  <ActiveGridScope gridId={LIST_GRID_ID} onAdd={handleAdd} onDelete={handleDelete}>
                    <GridPanel>
                      <ActiveGridHeader gridId={LIST_GRID_ID}>결의 목록</ActiveGridHeader>
                      <GridBody>
                        <HiGridComponent
                          ref={listGridRef}
                          columns={listColumns}
                          data={[]}
                          height="100%"
                          showRowNumber
                          rowNumberSize={56}
                          enableColumnFilters
                          enableContextMenu
                          enableHeaderMenu
                          enableSelectionStats
                          enableFreeze
                          enableQuickExport
                          exportFileName="지출결의등록type2"
                          gridId={LIST_GRID_ID}
                          prefsUserId={prefsUserId}
                          headerActive={activeGridId === LIST_GRID_ID}
                          {...listGrid.contextMenuProps}
                        />
                      </GridBody>
                    </GridPanel>
                  </ActiveGridScope>
                </GridExportFocusScope>
              </GridExportProvider>
            </ListHost>
            <EditorHost ref={hostRef} />
          </Stack>
          <CodeLookupDialog
            open={lookup.open}
            title={lookupConfig.title}
            columns={lookupConfig.columns}
            rows={lookupConfig.rows}
            enableSearch
            searchConfig={lookupConfig.searchConfig}
            prefsUserId={prefsUserId}
            sizeKey={`codeLookup-res-t2-${lookup.type || 'x'}`}
            onSelect={onLookupSelect}
            onClose={() => setLookup({ open: false, type: '' })}
          />
          {datePick.open && (
            <DateCalendarPopover
              mode="single"
              value={datePick.value}
              showTodayShortcut
              showPresets={false}
              yearPreset="calendar"
              anchorRect={calendarAnchorRect(hostRef.current)}
              onClose={() => setDatePick({ open: false, fieldId: '', value: '' })}
              onSelect={(stor) => {
                const fieldId = datePick.fieldId;
                setDatePick({ open: false, fieldId: '', value: '' });
                if (!fieldId || !stor) return;
                applyFields({ [fieldId]: formatDate(stor) });
              }}
            />
          )}
          <AmountCalcDialog
            open={amountPick.open}
            payTotalAmt={amountPick.payTotalAmt}
            fee={amountPick.fee}
            focusField={amountPick.focus}
            prefsUserId={prefsUserId}
            onClose={() => setAmountPick({ open: false, focus: 'pay', payTotalAmt: 0, fee: 0 })}
            onApply={(parts) => {
              setAmountPick({ open: false, focus: 'pay', payTotalAmt: 0, fee: 0 });
              applyFields(amountPartsToFieldValues(parts));
            }}
          />
        </Page>
    </FormProvider>
  );
}
